import { Directive, ElementRef, HostListener, Input, OnDestroy, TemplateRef, ViewContainerRef } from '@angular/core';
import {
  ConnectedOverlayPositionChange,
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  Overlay,
  OverlayRef,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Subscription } from 'rxjs';

/** Supported tooltip positions (mirrors CDK connected-overlay language). */
export type MiqTooltipPosition = 'above' | 'below' | 'before' | 'after';

/** All direction classes managed by the directive — used to clear stale ones on flip. */
const POSITION_CLASSES: readonly string[] = [
  'miq-tooltip--below',
  'miq-tooltip--above',
  'miq-tooltip--after',
  'miq-tooltip--before',
];

/**
 * Derives the correct `miq-tooltip--{direction}` class from the position pair
 * that CDK actually applied.  Called on every `positionChanges` emission so the
 * arrow tracks the real panel side even when CDK flips the tooltip on scroll.
 */
function positionClass(pair: ConnectedOverlayPositionChange['connectionPair']): string {
  if (pair.overlayY === 'top') return 'miq-tooltip--below';
  if (pair.overlayY === 'bottom') return 'miq-tooltip--above';
  if (pair.overlayX === 'start') return 'miq-tooltip--after';
  return 'miq-tooltip--before';
}

/**
 * Generic tooltip directive that renders any Angular template in a CDK
 * overlay panel when the host element is hovered.
 *
 * Because the content is a live `TemplateRef`, it can contain anything:
 * plain text, styled HTML, other Angular components, forms, maps, etc.
 *
 * Usage
 * -----
 * ```html
 * <span
 *   [miqTooltip]="myTip"
 *   miqTooltipClass="my-custom-class"
 *   miqTooltipPosition="below"
 *   [miqTooltipShowDelay]="300">
 *   hover me
 * </span>
 *
 * <ng-template #myTip>
 *   <div>Any content — components, maps, emoji 🗺️ — all supported.</div>
 * </ng-template>
 * ```
 *
 * Styling
 * -------
 * Add `miqTooltipClass` to scope styles to a specific instance.
 * The CDK overlay pane also receives `miq-tooltip--{position}` so arrow
 * pseudo-elements can be CSS-targeted per direction without JS.
 * This class is updated reactively whenever CDK flips the panel on scroll.
 *
 * Interactive content
 * -------------------
 * When the pointer moves from the trigger into the overlay panel the tooltip
 * stays visible (useful for forms, links, interactive maps).  It only hides
 * once the pointer leaves both the trigger AND the panel.
 */
@Directive({
  selector: '[miqTooltip]',
  standalone: true,
})
export class MiqTooltipDirective implements OnDestroy {
  /** The `ng-template` to render as tooltip content. Setting to `null` disables the tooltip. */
  @Input('miqTooltip') tooltipTemplate: TemplateRef<any> | null = null;

  /**
   * One or more CSS classes added to the CDK overlay pane element.
   * Use these to scope tooltip body styles to a specific instance.
   */
  @Input('miqTooltipClass') tooltipClass: string | string[] = '';

  /** Inline styles applied directly to the CDK overlay pane element. */
  @Input('miqTooltipStyles') tooltipStyles: Record<string, string> = {};

  /** Where the tooltip appears relative to the trigger element. */
  @Input('miqTooltipPosition') position: MiqTooltipPosition = 'below';

  /** Milliseconds to wait before showing the tooltip after mouse-enter. */
  @Input('miqTooltipShowDelay') showDelay = 300;

  /**
   * Milliseconds to wait before hiding after mouse-leave.
   * Must be long enough for the pointer to travel from the trigger into the
   * overlay panel without the tooltip blinking away.
   */
  @Input('miqTooltipHideDelay') hideDelay = 100;

  private overlayRef: OverlayRef | null = null;
  private positionSub: Subscription | null = null;
  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly overlay: Overlay,
    private readonly viewContainerRef: ViewContainerRef
  ) {}

  // ── Trigger mouse events ──────────────────────────────────────────────────

  @HostListener('mouseenter')
  onTriggerEnter(): void {
    this.clearHideTimer();
    if (!this.tooltipTemplate) return;
    this.showTimer = setTimeout(() => this.show(), this.showDelay);
  }

  @HostListener('mouseleave')
  onTriggerLeave(): void {
    this.clearShowTimer();
    this.hideTimer = setTimeout(() => this.hide(), this.hideDelay);
  }

  // ── Overlay lifecycle ─────────────────────────────────────────────────────

  private show(): void {
    if (!this.tooltipTemplate || this.overlayRef?.hasAttached()) return;

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.elementRef)
      .withPositions(this.buildPositions())
      .withPush(false);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });

    const portal = new TemplatePortal(this.tooltipTemplate, this.viewContainerRef);
    this.overlayRef.attach(portal);

    const pane = this.overlayRef.overlayElement;
    this.applyPaneStyles(pane);

    // React to CDK position flips so the arrow always points the right way.
    this.positionSub = (positionStrategy as FlexibleConnectedPositionStrategy).positionChanges.subscribe(change => {
      POSITION_CLASSES.forEach(c => pane.classList.remove(c));
      pane.classList.add(positionClass(change.connectionPair));
    });

    // Keep tooltip open when pointer moves into the panel.
    pane.addEventListener('mouseenter', () => this.onPaneEnter());
    pane.addEventListener('mouseleave', () => this.onPaneLeave());
  }

  private hide(): void {
    this.positionSub?.unsubscribe();
    this.positionSub = null;
    this.overlayRef?.detach();
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  // ── Panel mouse events ────────────────────────────────────────────────────

  private onPaneEnter(): void {
    this.clearHideTimer();
  }

  private onPaneLeave(): void {
    this.hideTimer = setTimeout(() => this.hide(), this.hideDelay);
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private applyPaneStyles(pane: HTMLElement): void {
    // Set the initial direction class — will be corrected by positionChanges if CDK flips.
    pane.classList.add(`miq-tooltip--${this.position}`);

    // Consumer-provided classes.
    const classes = Array.isArray(this.tooltipClass) ? this.tooltipClass : this.tooltipClass ? [this.tooltipClass] : [];
    classes.forEach(c => pane.classList.add(c));

    // Consumer-provided inline styles.
    Object.entries(this.tooltipStyles).forEach(([prop, val]) => {
      pane.style.setProperty(prop, val);
    });
  }

  /**
   * Builds a primary + automatic fallback position so the tooltip flips sides
   * when there is not enough room in the preferred direction.
   */
  private buildPositions(): ConnectedPosition[] {
    const positions: Record<MiqTooltipPosition, ConnectedPosition[]> = {
      below: [
        { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
        { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
      ],
      above: [
        { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
        { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
      ],
      after: [
        { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8 },
        { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -8 },
      ],
      before: [
        { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -8 },
        { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8 },
      ],
    };
    return positions[this.position];
  }

  private clearShowTimer(): void {
    if (this.showTimer !== null) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
  }

  private clearHideTimer(): void {
    if (this.hideTimer !== null) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.clearShowTimer();
    this.clearHideTimer();
    this.hide();
  }
}
